<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper, debounce } from '@/mixin/helper'
import { GET_REPORT_SCHEDULERS, DELETE_REPORT_SCHEDULER } from '@/services/api/report/report'
import { mapGetters } from 'vuex'
import i18n from '../../../../i18n'
import reportList from '../lib/reportList'
import { scopeReportScheduler } from '../lib/reportKeyLists'

export default {
  page: {
    title: 'Penjadwalan Laporan',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
  },
  mixins: [Helper],
  apollo: {
    reportSchedulers: {
      query: GET_REPORT_SCHEDULERS,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 10,
            },
            reportKey: this.reportOptions
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.totalRows = data.reportSchedulers.reportSchedulers.pagination.total
          this.reportSchedulers = data.reportSchedulers.reportSchedulers
          this.isLoading = false
        }
      },
    },
  },
  data() {
    return {
      phaseOneShow: false,
      reportSchedulers: {
        data: [],
      },
      filterSelected: [],
      selectAll: false,
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      filteredItems: [],
      isLoading: true,
    }
  },
  computed: {
    title() {
      return this.$t('report.object.scheduleReport')
    },
    isDomainView() {
      return this.hasAccess(['tms:create', 'tms:read', 'tms:update', 'tms:delete'])
    },
    fields() {
      return [
        {
          key: 'check',
          label: '',
          headerRowspan: 2,
        },
        {
          key: 'scheduleName',
          label: this.$t('report.title.scheduleName'),
          width: 30,
          headerRowspan: 2,
        },
        {
          key: 'reportName',
          label: this.$t('report.title.reportType'),
          width: 30,
          headerRowspan: 2,
        },
        {
          key: 'recipients',
          label: this.$t('notification.setting.emailNotification.recipientAmount'),
          width: 60,
          children: [
            {
              key: 'whatsapp',
              label: 'WhatsApp',
            },
            {
              key: 'email',
              label: 'Email',
            },
          ],
          headerColspan: 2,
          headerRowspan: 1,
        },
        {
          key: 'startTime',
          label: this.$t('report.fields.startTime'),
          width: 30,
          headerRowspan: 2,
        },
        {
          key: 'periodLabel',
          label: this.$t('report.title.deliveryPeriod'),
          width: 30,
          headerRowspan: 2,
        },
      ]
    },
    listReports() {
      return reportList.filter((report) => report.schedulerEnum !== undefined)
    },
    reportOptions() {
      let reportOptionsHasAccess = []
      this.listReports.forEach((r) => {
        if (!scopeReportScheduler[r.key] || this.hasAccessAll(scopeReportScheduler[r.key])) {
          reportOptionsHasAccess.push(r)
        }
      })

      reportOptionsHasAccess.sort((x, y) => {
        if (x.label < y.label) {
          return -1
        }
        if (x.label > y.label) {
          return 1
        }
        return 0
      })

      const pluck = (arr, key) => arr.map((i) => i[key])

      return pluck(reportOptionsHasAccess, 'schedulerEnum')
    },
    childFields() {
      const filteredData = this.fields.filter((obj) => {
        return Object.hasOwn(obj, 'children')
      })

      return filteredData
    },
    fieldList() {
      return this.selectValues(this.fields, 'key')
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },
  created() {
    i18n.locale = localStorage.getItem('language')
  },
  mounted() {
    this.$apollo.queries.reportSchedulers.refetch()
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    selectValues(data, key) {
      const result = []

      // Recursive helper function
      function selectHelper(obj) {
        const selectedValues = obj[key]
        if (selectedValues) {
          result.push(selectedValues)
        }

        if (obj.children && Array.isArray(obj.children)) {
          obj.children.forEach((child) => selectHelper(child))
        }
      }

      data.forEach((obj) => selectHelper(obj))

      for (let i = 0; i < result.length; i++) {
        if (result[i] === 'recipients') {
          result.splice(i, 1)
        }
      }

      return result
    },
    searchData: debounce(function () {
      this.onFiltered()
    }, 500),
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      if (this.filter.length > 0 && this.filter.length < 3) return
      this.currentPage = 1
      this.$apollo.queries.reportSchedulers.refetch({
        filter: {
          search: this.filter,
          pagination: {
            page: 1,
            show: this.perPage,
          },
          reportKey: this.reportOptions
        },
      })
    },
    changePerPage(perPage) {
      this.perPage = perPage
      if (this.filter == null) {
        this.$apollo.queries.reportSchedulers.refetch({
          filter: {
            pagination: {
              page: 1,
              show: this.perPage,
            },
            reportKey: this.reportOptions
          },
        })
      } else {
        this.$apollo.queries.reportSchedulers.refetch({
          filter: {
            search: this.filter,
            pagination: {
              page: 1,
              show: this.perPage,
            },
            reportKey: this.reportOptions
          },
        })
      }
    },
    changePage(page) {
      this.currentPage = page
      if (this.filter == null) {
        this.$apollo.queries.reportSchedulers.refetch({
          filter: {
            pagination: {
              page: this.currentPage,
              show: this.perPage,
            },
            reportKey: this.reportOptions
          },
        })
      } else {
        this.$apollo.queries.reportSchedulers.refetch({
          filter: {
            search: this.filter,
            pagination: {
              page: this.currentPage,
              show: this.perPage,
            },
            reportKey: this.reportOptions
          },
        })
      }
    },
    async getAllData() {
      let filterScheduler = null
      if (this.filter == null) {
        filterScheduler = {
          pagination: {
            page: 1,
            show: this.totalRows,
          },
          reportKey: this.reportOptions
        }
      } else {
        filterScheduler = {
          search: this.filter,
          pagination: {
            page: 1,
            show: this.totalRows,
          },
          reportKey: this.reportOptions
        }
      }
      let schedulerData = await this.$apollo.query({
        query: GET_REPORT_SCHEDULERS,
        fetchPolicy: 'no-cache',
        variables: {
          filter: filterScheduler,
        },
      })
      const canDelete = schedulerData.data.reportSchedulers.reportSchedulers.data.filter(
        (obj) => obj.canDelete === true
      )
      return canDelete
    },
    async toggleAll(event) {
      let checked = event.target.checked
      const data = await this.getAllData()
      if (checked) {
        for (const iterator of data) {
          this.filterSelected.push(iterator.reportSchedulerId)
        }
        this.filterSelected = [...new Set(this.filterSelected)]
      } else {
        for (const iterator of data) {
          let index = this.filterSelected.indexOf(iterator.reportSchedulerId)
          if (index !== -1) {
            this.filterSelected.splice(index, 1)
          }
        }
      }
    },
    async checkTable(event) {
      const data = await this.getAllData()
      if (event.target.checked) {
        this.filterSelected.push(parseInt(event.target.value))
        this.filterSelected = [...new Set(this.filterSelected)]
        let flg = true
        for (const iterator of data) {
          let index = this.filterSelected.indexOf(iterator.reportSchedulerId)
          if (index == -1) {
            flg = false
            break
          }
        }
        if (flg) this.selectAll = true
        else this.selectAll = false
      } else {
        this.selectAll = false
        let index = this.filterSelected.indexOf(parseInt(event.target.value))
        if (index !== -1) {
          this.filterSelected.splice(index, 1)
        }
      }
    },
    clearTable() {
      this.filterSelected = []
      this.selectAll = false
    },
    toDelete(bvModalEvent) {
      bvModalEvent.preventDefault()

      let promises = []
      for (const iterator of this.filterSelected) {
        promises.push(
          this.$apollo.mutate({
            mutation: DELETE_REPORT_SCHEDULER,
            variables: {
              input: {
                reportSchedulerIds: iterator,
              },
            },
          })
        )
      }

      Promise.all(promises).then(() => {
        this.$apollo.queries.reportSchedulers.refetch()
        this.$mcToast.success(this.$t('global.alert.text.delete'))
        this.clearTable()
        this.$bvModal.hide('modal-delete')
      })
    },
    onRowClicked(item) {
      if (!this.hasAccess(['a5a-penjadwalan-report:update'])) {
        return
      }
      this.$router.push({
        name: 'Report Scheduler Update',
        params: { id: item.reportSchedulerId },
      })
    },
    generateRecipientsFromContacts(contacts) {
      let recipients = []
      for (const contact of contacts) {
        if (contact.contact.email) {
          recipients.push(contact.contact.email)
        }
        if (contact.contact.phoneNumber) {
          recipients.push(contact.contact.phoneNumber)
        }
      }
      return recipients.join(', ')
    },
  },
}
</script>
<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" />
      <p class="mb-4">{{ $t('report.text.howOften') }}</p>
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <b-button
            @click="
              $router.push({
                name: 'Report',
                query: { domain: 'fleet' },
              })
            "
            variant="outline-primary"
            class="ms-3"
          >
            <i class="mc-fa icon-outline_arrow-left"></i>
            {{ $t('global.back') }}
          </b-button>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-12">
          <div class="mc-table-filter text-end">
            <b-button
              v-if="filterSelected.length"
              variant="outline-danger"
              class="mx-2"
              v-b-modal.modal-delete
            >
              <i class="mc-fa icon-outline_trash"></i> {{ $t('global.delete') }}
            </b-button>
            <b-button
              v-if="hasAccess(['a5a-penjadwalan-report:create'])"
              variant="primary"
              @click="$router.push({ name: 'Report Scheduler Create' })"
            >
              <i class="mc-fa icon-outline_plus"></i>
              {{ $t('report.title.reschedule') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-4 col-md-4 mc-row-check-wrapper">
            <h6 v-if="filterSelected.length" class="mc-row-check">
              <span class="badge btn-success">{{ filterSelected.length }}</span>
              {{ $t('global.object.selectedRow') }}
              <a class="color-primary" @click="clearTable">{{ $t('global.action.clearAll') }}</a>
            </h6>
          </div>

          <div class="col-sm-8 col-md-8 d-flex flex-row-reverse mc-table-view-wrapper">
            <div class="mc-table-view">
              <div id="tickets-table_filter" class="dataTables_filter text-md-end">
                <label
                  class="d-inline-flex align-items-center mc-table-view__search"
                  @click="focusSearch"
                >
                  <i class="mc-fa fas fa-search"></i>
                  <b-form-input
                    v-model="filter"
                    type="search"
                    :placeholder="$t('global.search') + '...'"
                    class="ms-2"
                    @input="searchData"
                    ref="searchInput"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="table-responsive my-3 border border-1">
          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <b-card>
                <b-skeleton-table
                  :rows="3"
                  :columns="fields.length"
                  :table-props="{ bordered: false, striped: true }"
                />
              </b-card>
            </template>
            <table class="table b-table mc-table--report-scheduler">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    id="header"
                    :key="field.key"
                    :colspan="field.headerColspan"
                    :rowspan="field.headerRowspan"
                  >
                    <div v-if="field.key === 'check'">
                      <div class="form-check">
                        <input
                          class="form-check-input me-1"
                          type="checkbox"
                          value="all"
                          v-model="selectAll"
                          @input="toggleAll"
                        />
                      </div>
                    </div>
                    <div v-else :style="{ 'text-align': field.headerColspan > 1 ? 'center' : '' }">
                      {{ field.label }}
                    </div>
                  </th>
                </tr>
                <tr>
                  <th
                    v-for="field in childFields[0].children"
                    :key="field.key"
                    class="sub-header"
                    :colspan="field.headerColspan"
                  >
                    {{ field.label }}
                  </th>
                </tr>
              </thead>

              <tbody v-if="reportSchedulers.data">
                <tr v-for="data in reportSchedulers.data" :key="data.reportSchedulerId">
                  <td v-for="key in fieldList" :key="key">
                    <div v-if="key === 'check'">
                      <div class="form-check">
                        <input
                          class="form-check-input me-1"
                          type="checkbox"
                          :value="data.reportSchedulerId"
                          v-model="filterSelected"
                          @input="checkTable"
                          :disabled="!data.canDelete"
                        />
                      </div>
                    </div>

                    <span
                      v-if="key === 'scheduleName'"
                      class="fw-bold text-primary td__noellipsis clickable-field"
                      @click="onRowClicked(data)"
                    >
                      {{ data[key] ?? '-' }}
                    </span>

                    <span v-if="key === 'whatsapp' || key === 'email'">
                      {{ data.aggregate[key] }}
                    </span>

                    <span v-if="key === 'reportName'">
                      {{ $t(listReports.find((item) => item.key == data.reportKey)?.label) }}
                    </span>

                    <span v-if="key === 'periodLabel'">
                      {{ data[key] }}
                    </span>

                    <span v-if="key === 'startTime'">
                      {{ data[key] ? momentDateLongMonth(data[key]) : '-' }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-if="reportSchedulers.data.length == 0">
                <tr>
                  <td colspan="7">
                    <div class="text-center my-4">
                      {{ $t('global.emptyTable') }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-skeleton-wrapper>
        </div>

        <div class="row">
          <div class="col-sm-12 col-md-6">
            <div id="tickets-table_length" class="dataTables_length">
              <label class="d-inline-flex align-items-center">
                {{ $t('global.text.displays') }}
                <b-form-select
                  class="form-select form-select-sm"
                  v-model="perPage"
                  size="sm"
                  :options="pageOptions"
                  @input="changePerPage"
                />
                {{ $t('global.text.entries') }}.
                <p>
                  {{ $t('global.text.display') }}&nbsp;<strong>{{
                    totalRows > 0 ? (currentPage - 1) * perPage + 1 : 0
                  }}</strong
                  >&nbsp;{{ $t('global.object.to') }}&nbsp;<strong>{{
                    Math.min(totalRows, currentPage * perPage)
                  }}</strong
                  >&nbsp;{{ $t('global.object.of') }}&nbsp;<strong>{{ totalRows }}</strong
                  >&nbsp;{{ $t('global.text.entries') }}.
                </p>
              </label>
            </div>
          </div>
          <div class="col-sm-12 col-md-6">
            <div class="dataTables_paginate paging_simple_numbers float-end">
              <ul class="pagination pagination-rounded mb-0">
                <!-- pagination -->
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows == null ? rows : totalRows"
                  :per-page="perPage"
                  @change="changePage"
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal class="mc-modal" id="modal-delete" title="Hapus" size="md" centered scrollable>
      <div class="card-body mc-modal__filter p-2">
        <p>
          {{ $t('report.text.areYouSure') }} {{ filterSelected.length }}
          {{ $t('report.text.data') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer mx-0">
          <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel()" class="me-2">
              {{ $t('global.action.no') }}
            </b-button>
            <b-button @click="toDelete" variant="primary"> {{ $t('global.action.yes') }} </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </Layout>
</template>
<style lang="scss" scoped>
.mc-table--report-scheduler {
  .sub-header {
    width: 120px !important;

    &:last-child {
      border-right: 1px solid $teal-100;
    }
  }
  .clickable-field {
    cursor: pointer;
  }
}
</style>
